import { useMemo } from "react";
import { JobEventsBoard } from "./board/JobEventsBoard";
import { JobEventsBoardProvider } from "./board/context";
import { AddVisitButton } from "./layout/AddVisitButton";
import { JobEventsHeader } from "./layout/JobEventsHeader";
import { JobEventsLayout } from "./layout/JobEventsLayout";
import { useJobEventsController } from "./hooks/useJobEventController";
import { useJobEventsPaginated } from "./hooks/useJobEventsPaginated";
import { Job } from "../../interfaces/job.interfaces";
import type { TimeRangePreset } from "./board/filters/job-event-filters.types";

/* ───── Resolve a TimeRangePreset into ISO start/end dates ───── */

function computeDateRange(preset?: TimeRangePreset, customStart?: string, customEnd?: string): { startDate?: string; endDate?: string } {
  if (!preset) return {};

  if (preset === "CUSTOM") {
    return {
      startDate: customStart ? new Date(`${customStart}T00:00:00`).toISOString() : undefined,
      endDate: customEnd ? new Date(`${customEnd}T23:59:59.999`).toISOString() : undefined,
    };
  }

  const now = new Date();

  if (preset === "THIS_WEEK") {
    const day = now.getDay();
    const diff = now.getDate() - day + (day === 0 ? -6 : 1);
    const start = new Date(now.getFullYear(), now.getMonth(), diff, 0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    end.setHours(23, 59, 59, 999);
    return { startDate: start.toISOString(), endDate: end.toISOString() };
  }

  if (preset === "THIS_MONTH") {
    const start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
    return { startDate: start.toISOString(), endDate: end.toISOString() };
  }

  if (preset === "NEXT_MONTH") {
    const start = new Date(now.getFullYear(), now.getMonth() + 1, 1, 0, 0, 0, 0);
    const end = new Date(now.getFullYear(), now.getMonth() + 2, 0, 23, 59, 59, 999);
    return { startDate: start.toISOString(), endDate: end.toISOString() };
  }

  return {};
}

/* ───── Component ───── */

interface Props {
  job: Job;
}

export function JobsEvents({ job }: Props) {
  const controller = useJobEventsController(job);

  const { startDate, endDate } = useMemo(
    () => computeDateRange(controller.timeRange, controller.customStartDate, controller.customEndDate),
    [controller.timeRange, controller.customStartDate, controller.customEndDate],
  );

  const eventsQuery = useJobEventsPaginated({
    parentId: job.id,
    pageSize: 5,
    order: "RELEVANCE",
    status: controller.status,
    search: controller.search,
    startDate,
    endDate,
    billingStatus: controller.billingStatus,
  });

  return (
    <JobEventsLayout
      titleAction={<AddVisitButton job={job} />}
      header={
        <JobEventsHeader
          title="Visits"
          search={controller.search}
          onSearchChange={controller.setSearch}
          sort={controller.status}
          onSortChange={controller.setStatus}
        />
      }
    >
      <JobEventsBoardProvider>
        <JobEventsBoard controller={controller} eventsQuery={eventsQuery} />
      </JobEventsBoardProvider>
    </JobEventsLayout>
  );
}
